// src/pages/KitchenWallMeasure.js
//
// Focused per-wall measurement for Kitchen Mode.
// Flow: upload photo → AI Stick calibrate (manual-tap fallback) → tap the two
// ends of the wall (LENGTH, required) → optionally measure WALL HEIGHT → Done
// returns { lengthFt, heightFt|null } to the wall slot.
//
// Wall height is intentionally optional.
// If skipped, heightFt remains null and downstream Kitchen geometry/modeling
// uses the standard assumed 8' wall height.
//
// Adapts the proven TapToMeasure mechanics (calibration ladder, precision loupe,
// drag-to-reposition, arrow-key nudge), trimmed to one required measurement plus
// an optional second. Duplication with TapToMeasure is intentional (speed).

import React, { useEffect, useRef, useState } from "react";
import API_BASE from "../api/apiBase";
import "./KitchenWallMeasure.css";

const STICK_LENGTH_INCHES = 11;

// ── Guard 1: snap ──────────────────────────────────────────────────────────
// A measurement line within SNAP_DEG of horizontal/vertical can be snapped
// exactly level/plumb. Currently disabled to preserve the existing behavior.
const SNAP_ENABLED = false;
const SNAP_DEG = 5;

// ── Guard 2: capture guidance ──────────────────────────────────────────────
const CAPTURE_WARN_PPI = 40;
const CAPTURE_MIN_DIM = 1200;

const snapPoint = (p1, p2) => {
  if (!SNAP_ENABLED) return p2;
  if (!p1 || !p2) return p2;

  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  if (dx === 0 && dy === 0) return p2;

  const angle = Math.abs(
    (Math.atan2(dy, dx) * 180) / Math.PI
  );

  if (
    angle <= SNAP_DEG ||
    angle >= 180 - SNAP_DEG
  ) {
    return {
      x: p2.x,
      y: p1.y,
    };
  }

  if (
    Math.abs(angle - 90) <= SNAP_DEG
  ) {
    return {
      x: p1.x,
      y: p2.y,
    };
  }

  return p2;
};

const COLORS = {
  stick: "#f59e0b",
  length: "#0057b8",
  height: "#c026d3",
};

const clamp = (v, min, max) =>
  Math.min(
    max,
    Math.max(min, v)
  );

const distance = (p1, p2) => {
  if (!p1 || !p2) return 0;

  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;

  return Math.sqrt(
    dx * dx + dy * dy
  );
};

const feetInches = (feet) => {
  if (
    feet == null ||
    !isFinite(feet)
  ) {
    return "—";
  }

  const totalIn =
    Math.round(feet * 12);

  const ft =
    Math.floor(
      totalIn / 12
    );

  const inch =
    totalIn % 12;

  if (
    ft === 0 &&
    inch === 0
  ) {
    return "0″";
  }

  if (inch === 0) {
    return `${ft}′`;
  }

  if (ft === 0) {
    return `${inch}″`;
  }

  return `${ft}′ ${inch}″`;
};

export default function KitchenWallMeasure({
  wallLabel = "Wall",
  onComplete,
  onCancel,
}) {
  const canvasRef =
    useRef(null);

  const zoomRef =
    useRef(null);

  const floatZoomRef =
    useRef(null);

  const imgRef =
    useRef(null);

  const fileRef =
    useRef(null);

  const dragRef =
    useRef(null);

  const downRef =
    useRef(null);

  const [touchActive, setTouchActive] =
    useState(false);

  const [imageLoaded, setImageLoaded] =
    useState(false);

  // mode:
  // calibrate | length | height | done
  const [mode, setMode] =
    useState("calibrate");

  const [stickPoints, setStickPoints] =
    useState([]);

  const [
    pixelsPerInch,
    setPixelsPerInch,
  ] = useState(null);

  const [
    calibrationSource,
    setCalibrationSource,
  ] = useState(null);

  const [
    lengthPoints,
    setLengthPoints,
  ] = useState([]);

  const [
    heightPoints,
    setHeightPoints,
  ] = useState([]);

  const [
    lengthFt,
    setLengthFt,
  ] = useState(null);

  const [
    heightFt,
    setHeightFt,
  ] = useState(null);

  const [
    wantHeight,
    setWantHeight,
  ] = useState(false);

  const [
    hoverPoint,
    setHoverPoint,
  ] = useState(null);

  const [
    activePoint,
    setActivePoint,
  ] = useState(null);

  const [
    hoverGrab,
    setHoverGrab,
  ] = useState(null);

  const [
    notice,
    setNotice,
  ] = useState(null);

  const [
    busy,
    setBusy,
  ] = useState(false);

  const [
    capture,
    setCapture,
  ] = useState(null);

  /* =========================================================
     CALIBRATION
     ========================================================= */

  const autoCalibrate =
    async (file, img) => {
      try {
        if (!file || !img) {
          return null;
        }

        const fd =
          new FormData();

        fd.append(
          "image",
          file
        );

        const res =
          await fetch(
            `${API_BASE}/api/detect-stick`,
            {
              method: "POST",
              body: fd,
            }
          );

        if (!res.ok) {
          return null;
        }

        const data =
          await res.json();

        if (
          !data?.boundingBox
        ) {
          return null;
        }

        const bb =
          data.boundingBox;

        const widthPx =
          bb.width *
          img.width;

        const yC =
          (
            bb.top +
            bb.height / 2
          ) *
          img.height;

        const p1 = {
          x:
            bb.left *
            img.width,

          y: yC,
        };

        const p2 = {
          x:
            (
              bb.left +
              bb.width
            ) *
            img.width,

          y: yC,
        };

        const roundedPx =
          Math.round(
            widthPx * 2
          ) / 2;

        setStickPoints([
          p1,
          p2,
        ]);

        setPixelsPerInch(
          roundedPx /
            STICK_LENGTH_INCHES
        );

        setCalibrationSource(
          "ai"
        );

        return roundedPx;
      } catch (e) {
        console.error(
          "AI calibrate failed:",
          e
        );

        return null;
      }
    };

  const handleUpload = (
    file
  ) => {
    if (!file) return;

    const url =
      URL.createObjectURL(
        file
      );

    const img =
      new Image();

    img.onload =
      async () => {
        imgRef.current =
          img;

        setImageLoaded(
          true
        );

        // Reset measurement state.
        setStickPoints([]);
        setPixelsPerInch(null);
        setCalibrationSource(null);

        setLengthPoints([]);
        setHeightPoints([]);

        setLengthFt(null);
        setHeightFt(null);

        setWantHeight(false);

        setActivePoint(null);
        setHoverPoint(null);

        dragRef.current =
          null;

        downRef.current =
          null;

        setCapture(null);

        setMode(
          "calibrate"
        );

        const minDim =
          Math.min(
            img.width,
            img.height
          );

        const lowRes =
          minDim <
          CAPTURE_MIN_DIM;

        setBusy(true);

        const stickPx =
          await autoCalibrate(
            file,
            img
          );

        setBusy(false);

        if (stickPx) {
          const ppi =
            stickPx /
            STICK_LENGTH_INCHES;

          if (lowRes) {
            setCapture({
              level:
                "warn",

              title:
                "Low-resolution photo",

              text:
                `Low-resolution photo (${img.width}×${img.height}). ` +
                "The AI Stick was detected, so you can proceed — " +
                "but measurements will be coarser than usual. " +
                "For best accuracy, retake at a higher resolution.",
            });
          } else if (
            ppi <
            CAPTURE_WARN_PPI
          ) {
            setCapture({
              level:
                "warn",

              title:
                "Stick is small in frame",

              text:
                `The AI Stick is small in this photo (${ppi.toFixed(0)} px/in). ` +
                "Measurements may read long. For best accuracy, move closer and retake.",
            });
          }

          setMode(
            "length"
          );

          setNotice(
            "AI Stick detected. Now tap the two ends of the wall."
          );
        } else {
          if (lowRes) {
            setCapture({
              level:
                "block",

              title:
                "Photo too low resolution",

              text:
                `This photo is low resolution (${img.width}×${img.height}) ` +
                "and the AI Stick couldn't be detected. Measurements won't be reliable — " +
                "retake it at a higher resolution.",
            });
          }

          setNotice(
            "AI Stick not detected — tap the two ends of the stick to calibrate manually."
          );
        }

        try {
          URL.revokeObjectURL(
            url
          );
        } catch (_) {
          // Ignore cleanup errors.
        }
      };

    img.onerror =
      () => {
        setNotice(
          "Couldn't load that image. Try another."
        );
      };

    img.src =
      url;
  };

  /* =========================================================
     DRAWING
     ========================================================= */

  useEffect(() => {
    if (
      !imageLoaded ||
      !canvasRef.current ||
      !imgRef.current
    ) {
      return;
    }

    const canvas =
      canvasRef.current;

    if (
      canvas.width !==
        imgRef.current.width ||
      canvas.height !==
        imgRef.current.height
    ) {
      canvas.width =
        imgRef.current.width;

      canvas.height =
        imgRef.current.height;
    }

    const ctx =
      canvas.getContext(
        "2d"
      );

    ctx.clearRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    ctx.drawImage(
      imgRef.current,
      0,
      0,
      canvas.width,
      canvas.height
    );

    const rect =
      canvas.getBoundingClientRect();

    const k =
      rect.width > 0
        ? canvas.width /
          rect.width
        : 1;

    const drawLine = (
      pts,
      color,
      label
    ) => {
      if (
        !pts ||
        pts.length === 0
      ) {
        return;
      }

      ctx.save();

      ctx.strokeStyle =
        color;

      ctx.fillStyle =
        color;

      ctx.lineWidth =
        3 * k;

      ctx.lineCap =
        "round";

      if (
        pts.length === 1
      ) {
        ctx.beginPath();

        ctx.arc(
          pts[0].x,
          pts[0].y,
          6 * k,
          0,
          Math.PI * 2
        );

        ctx.fill();

        ctx.restore();

        return;
      }

      ctx.beginPath();

      ctx.moveTo(
        pts[0].x,
        pts[0].y
      );

      ctx.lineTo(
        pts[1].x,
        pts[1].y
      );

      ctx.stroke();

      [
        pts[0],
        pts[1],
      ].forEach((p) => {
        ctx.beginPath();

        ctx.arc(
          p.x,
          p.y,
          6 * k,
          0,
          Math.PI * 2
        );

        ctx.fill();
      });

      if (label) {
        const mx =
          (
            pts[0].x +
            pts[1].x
          ) / 2;

        const my =
          (
            pts[0].y +
            pts[1].y
          ) / 2;

        ctx.font =
          `bold ${14 * k}px sans-serif`;

        const tw =
          ctx.measureText(
            label
          ).width;

        const pad =
          6 * k;

        ctx.fillStyle =
          "rgba(0,0,0,0.7)";

        ctx.beginPath();

        ctx.roundRect(
          mx -
            tw / 2 -
            pad,

          my -
            12 * k -
            pad,

          tw +
            pad * 2,

          20 * k +
            pad,

          6 * k
        );

        ctx.fill();

        ctx.fillStyle =
          color;

        ctx.textAlign =
          "center";

        ctx.textBaseline =
          "middle";

        ctx.fillText(
          label,
          mx,
          my
        );
      }

      ctx.restore();
    };

    drawLine(
      stickPoints,
      COLORS.stick,
      stickPoints.length === 2
        ? `${STICK_LENGTH_INCHES}″`
        : null
    );

    drawLine(
      lengthPoints,
      COLORS.length,
      lengthFt != null
        ? feetInches(
            lengthFt
          )
        : null
    );

    drawLine(
      heightPoints,
      COLORS.height,
      heightFt != null
        ? feetInches(
            heightFt
          )
        : null
    );

    /* -------------------------------------------------------
       ACTIVE / HOVERED POINT
       ------------------------------------------------------- */

    const hl =
      dragRef.current ||
      hoverGrab;

    if (hl) {
      const arr =
        hl.kind ===
        "stick"
          ? stickPoints
          : hl.kind ===
            "length"
          ? lengthPoints
          : heightPoints;

      const p =
        arr[hl.index];

      if (p) {
        ctx.save();

        ctx.lineWidth =
          2 * k;

        ctx.strokeStyle =
          "#0057b8";

        ctx.beginPath();

        ctx.arc(
          p.x,
          p.y,
          10 * k,
          0,
          Math.PI * 2
        );

        ctx.stroke();

        ctx.restore();
      }
    }

    /* -------------------------------------------------------
       RUBBER-BAND PREVIEW
       ------------------------------------------------------- */

    if (
      hoverPoint &&
      !dragRef.current &&
      pixelsPerInch &&
      (
        (
          mode ===
            "length" &&
          lengthPoints.length ===
            1
        ) ||
        (
          mode ===
            "height" &&
          heightPoints.length ===
            1
        )
      )
    ) {
      const p1 =
        mode ===
        "length"
          ? lengthPoints[0]
          : heightPoints[0];

      const color =
        mode ===
        "length"
          ? COLORS.length
          : COLORS.height;

      const p2 =
        snapPoint(
          p1,
          hoverPoint
        );

      ctx.save();

      ctx.lineCap =
        "round";

      ctx.lineWidth =
        7 * k;

      ctx.strokeStyle =
        "rgba(255,255,255,0.7)";

      ctx.beginPath();

      ctx.moveTo(
        p1.x,
        p1.y
      );

      ctx.lineTo(
        p2.x,
        p2.y
      );

      ctx.stroke();

      ctx.lineWidth =
        3 * k;

      ctx.strokeStyle =
        color;

      ctx.beginPath();

      ctx.moveTo(
        p1.x,
        p1.y
      );

      ctx.lineTo(
        p2.x,
        p2.y
      );

      ctx.stroke();

      const live =
        feetInches(
          distance(
            p1,
            p2
          ) /
            pixelsPerInch /
            12
        );

      const mx =
        (
          p1.x +
          p2.x
        ) / 2;

      const my =
        (
          p1.y +
          p2.y
        ) / 2;

      ctx.font =
        `bold ${14 * k}px sans-serif`;

      const tw =
        ctx.measureText(
          live
        ).width;

      const pad =
        6 * k;

      ctx.fillStyle =
        "rgba(0,0,0,0.7)";

      ctx.beginPath();

      ctx.roundRect(
        mx -
          tw / 2 -
          pad,

        my -
          12 * k -
          pad,

        tw +
          pad * 2,

        20 * k +
          pad,

        6 * k
      );

      ctx.fill();

      ctx.fillStyle =
        color;

      ctx.textAlign =
        "center";

      ctx.textBaseline =
        "middle";

      ctx.fillText(
        live,
        mx,
        my
      );

      ctx.restore();
    }

    /* -------------------------------------------------------
       RETICLE
       ------------------------------------------------------- */

    if (
      hoverPoint &&
      (
        mode ===
          "calibrate" ||
        mode ===
          "length" ||
        mode ===
          "height"
      )
    ) {
      ctx.save();

      const color =
        mode ===
        "calibrate"
          ? COLORS.stick
          : mode ===
            "length"
          ? COLORS.length
          : COLORS.height;

      ctx.strokeStyle =
        color;

      ctx.lineWidth =
        1.5 * k;

      ctx.beginPath();

      ctx.arc(
        hoverPoint.x,
        hoverPoint.y,
        10 * k,
        0,
        Math.PI * 2
      );

      ctx.stroke();

      ctx.beginPath();

      ctx.moveTo(
        hoverPoint.x -
          14 * k,
        hoverPoint.y
      );

      ctx.lineTo(
        hoverPoint.x +
          14 * k,
        hoverPoint.y
      );

      ctx.moveTo(
        hoverPoint.x,
        hoverPoint.y -
          14 * k
      );

      ctx.lineTo(
        hoverPoint.x,
        hoverPoint.y +
          14 * k
      );

      ctx.stroke();

      ctx.restore();
    }

    /* -------------------------------------------------------
       PRECISION LOUPE
       ------------------------------------------------------- */

    const drawLoupe = (
      zcanvas
    ) => {
      if (!zcanvas) {
        return;
      }

      const zctx =
        zcanvas.getContext(
          "2d"
        );

      zctx.clearRect(
        0,
        0,
        zcanvas.width,
        zcanvas.height
      );

      if (
        hoverPoint &&
        imgRef.current
      ) {
        const src =
          imgRef.current;

        const rx =
          canvas.width > 0
            ? src.width /
              canvas.width
            : 1;

        const ry =
          canvas.height > 0
            ? src.height /
              canvas.height
            : 1;

        const hx =
          hoverPoint.x *
          rx;

        const hy =
          hoverPoint.y *
          ry;

        const ZOOM =
          2.5;

        const win =
          Math.round(
            zcanvas.width /
              ZOOM
          );

        const sx =
          clamp(
            hx -
              win / 2,

            0,

            Math.max(
              0,
              src.width -
                win
            )
          );

        const sy =
          clamp(
            hy -
              win / 2,

            0,

            Math.max(
              0,
              src.height -
                win
            )
          );

        zctx.imageSmoothingEnabled =
          true;

        zctx.imageSmoothingQuality =
          "high";

        zctx.drawImage(
          src,
          sx,
          sy,
          win,
          win,
          0,
          0,
          zcanvas.width,
          zcanvas.height
        );

        const cx =
          (
            (
              hx -
              sx
            ) /
            win
          ) *
          zcanvas.width;

        const cy =
          (
            (
              hy -
              sy
            ) /
            win
          ) *
          zcanvas.height;

        zctx.lineWidth =
          3;

        zctx.strokeStyle =
          "rgba(0,0,0,0.55)";

        zctx.beginPath();

        zctx.moveTo(
          cx - 12,
          cy
        );

        zctx.lineTo(
          cx + 12,
          cy
        );

        zctx.moveTo(
          cx,
          cy - 12
        );

        zctx.lineTo(
          cx,
          cy + 12
        );

        zctx.stroke();

        zctx.lineWidth =
          1.25;

        zctx.strokeStyle =
          "rgba(255,255,255,0.95)";

        zctx.beginPath();

        zctx.moveTo(
          cx - 12,
          cy
        );

        zctx.lineTo(
          cx + 12,
          cy
        );

        zctx.moveTo(
          cx,
          cy - 12
        );

        zctx.lineTo(
          cx,
          cy + 12
        );

        zctx.stroke();
      } else {
        zctx.strokeStyle =
          "#b0c4de";

        zctx.lineWidth =
          1;

        zctx.strokeRect(
          0.5,
          0.5,
          zcanvas.width -
            1,
          zcanvas.height -
            1
        );
      }
    };

    drawLoupe(
      zoomRef.current
    );

    drawLoupe(
      floatZoomRef.current
    );
  }, [
    imageLoaded,
    stickPoints,
    lengthPoints,
    heightPoints,
    lengthFt,
    heightFt,
    hoverPoint,
    hoverGrab,
    mode,
    pixelsPerInch,
  ]);

  /* =========================================================
     ARROW-KEY NUDGE
     ========================================================= */

  useEffect(() => {
    if (
      !activePoint ||
      !imageLoaded
    ) {
      return undefined;
    }

    const onKey = (e) => {
      const moves = {
        ArrowLeft:
          [-1, 0],

        ArrowRight:
          [1, 0],

        ArrowUp:
          [0, -1],

        ArrowDown:
          [0, 1],
      };

      if (!moves[e.key]) {
        return;
      }

      const tag =
        (
          document.activeElement &&
          document.activeElement
            .tagName
        ) || "";

      if (
        tag ===
          "INPUT" ||
        tag ===
          "TEXTAREA"
      ) {
        return;
      }

      e.preventDefault();

      const step =
        e.shiftKey
          ? 10
          : 1;

      const dx =
        moves[e.key][0] *
        step;

      const dy =
        moves[e.key][1] *
        step;

      nudge(
        dx,
        dy
      );
    };

    window.addEventListener(
      "keydown",
      onKey
    );

    return () =>
      window.removeEventListener(
        "keydown",
        onKey
      );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activePoint,
    imageLoaded,
    stickPoints,
    lengthPoints,
    heightPoints,
    pixelsPerInch,
  ]);

  /* =========================================================
     POINT MATH
     ========================================================= */

  const recompute = (
    kind,
    pts
  ) => {
    if (
      kind ===
        "stick" &&
      pts.length === 2
    ) {
      const roundedPx =
        Math.round(
          distance(
            pts[0],
            pts[1]
          ) * 2
        ) / 2;

      setPixelsPerInch(
        roundedPx /
          STICK_LENGTH_INCHES
      );
    } else if (
      kind ===
        "length" &&
      pts.length === 2 &&
      pixelsPerInch
    ) {
      setLengthFt(
        distance(
          pts[0],
          pts[1]
        ) /
          pixelsPerInch /
          12
      );
    } else if (
      kind ===
        "height" &&
      pts.length === 2 &&
      pixelsPerInch
    ) {
      setHeightFt(
        distance(
          pts[0],
          pts[1]
        ) /
          pixelsPerInch /
          12
      );
    }
  };

  const setKindPoints = (
    kind,
    pts
  ) => {
    if (
      kind ===
      "stick"
    ) {
      setStickPoints(
        pts
      );
    } else if (
      kind ===
      "length"
    ) {
      setLengthPoints(
        pts
      );
    } else {
      setHeightPoints(
        pts
      );
    }
  };

  const nudge = (
    dx,
    dy
  ) => {
    if (!activePoint) {
      return;
    }

    const canvas =
      canvasRef.current;

    const w =
      canvas?.width ??
      Infinity;

    const h =
      canvas?.height ??
      Infinity;

    const {
      kind,
      index,
    } =
      activePoint;

    const arr =
      kind ===
      "stick"
        ? stickPoints
        : kind ===
          "length"
        ? lengthPoints
        : heightPoints;

    if (!arr[index]) {
      return;
    }

    const pts =
      [...arr];

    pts[index] = {
      x:
        clamp(
          pts[index].x +
            dx,
          0,
          w
        ),

      y:
        clamp(
          pts[index].y +
            dy,
          0,
          h
        ),
    };

    setKindPoints(
      kind,
      pts
    );

    recompute(
      kind,
      pts
    );

    setHoverPoint(
      pts[index]
    );
  };

  const canvasPoint = (
    e
  ) => {
    const canvas =
      canvasRef.current;

    if (!canvas) {
      return null;
    }

    const rect =
      canvas.getBoundingClientRect();

    const x =
      (
        e.clientX -
        rect.left
      ) *
      (
        canvas.width /
        rect.width
      );

    const y =
      (
        e.clientY -
        rect.top
      ) *
      (
        canvas.height /
        rect.height
      );

    return {
      x:
        clamp(
          x,
          0,
          canvas.width
        ),

      y:
        clamp(
          y,
          0,
          canvas.height
        ),
    };
  };

  const findGrab = (
    pt
  ) => {
    if (!pt) {
      return null;
    }

    const canvas =
      canvasRef.current;

    const rect =
      canvas.getBoundingClientRect();

    const r =
      16 *
      (
        rect.width > 0
          ? canvas.width /
            rect.width
          : 1
      );

    const groups = [
      {
        kind:
          "stick",
        pts:
          stickPoints,
      },

      {
        kind:
          "length",
        pts:
          lengthPoints,
      },

      {
        kind:
          "height",
        pts:
          heightPoints,
      },
    ];

    let best =
      null;

    let bestD =
      r;

    for (
      const g of groups
    ) {
      for (
        let i = 0;
        i <
        g.pts.length;
        i += 1
      ) {
        const d =
          distance(
            g.pts[i],
            pt
          );

        if (
          d <= bestD
        ) {
          bestD =
            d;

          best = {
            kind:
              g.kind,

            index:
              i,
          };
        }
      }
    }

    return best;
  };

  /* =========================================================
     POINTER HANDLERS
     ========================================================= */

  const onPointerDown = (
    e
  ) => {
    if (!imageLoaded) {
      return;
    }

    const canvas =
      canvasRef.current;

    if (
      e.pointerId !=
        null &&
      canvas.setPointerCapture
    ) {
      try {
        canvas.setPointerCapture(
          e.pointerId
        );
      } catch (_) {
        // Ignore pointer capture errors.
      }
    }

    const pt =
      canvasPoint(e);

    if (!pt) {
      return;
    }

    const isTouch =
      e.pointerType ===
      "touch";

    const grab =
      findGrab(pt);

    if (isTouch) {
      downRef.current = {
        pt,
        grab,
        isTouch:
          true,
        moved:
          false,
      };

      setTouchActive(
        true
      );

      if (grab) {
        setActivePoint(
          grab
        );
      }

      setHoverPoint(
        pt
      );

      return;
    }

    if (grab) {
      dragRef.current =
        grab;

      setActivePoint(
        grab
      );

      setHoverPoint(
        pt
      );

      return;
    }

    placeAt(pt);
  };

  const placeAt = (
    pt
  ) => {
    if (
      mode ===
      "calibrate"
    ) {
      const next =
        stickPoints.length >=
        2
          ? [pt]
          : [
              ...stickPoints,
              pt,
            ];

      setStickPoints(
        next
      );

      setActivePoint({
        kind:
          "stick",

        index:
          next.length -
          1,
      });

      if (
        next.length === 2
      ) {
        const roundedPx =
          Math.round(
            distance(
              next[0],
              next[1]
            ) * 2
          ) / 2;

        const ppi =
          roundedPx /
          STICK_LENGTH_INCHES;

        setPixelsPerInch(
          ppi
        );

        setCalibrationSource(
          "manual"
        );

        if (
          ppi <
          CAPTURE_WARN_PPI
        ) {
          setCapture({
            level:
              "warn",

            title:
              "Stick is small in frame",

            text:
              `The AI Stick is small in this photo (${ppi.toFixed(0)} px/in). ` +
              "Measurements may read long. For best accuracy, move closer and retake.",
          });
        } else {
          setCapture(null);
        }

        setMode(
          "length"
        );

        setNotice(
          "Calibrated. Now tap the two ends of the wall."
        );
      }

      return;
    }

    if (
      mode ===
      "length"
    ) {
      const snapped =
        lengthPoints.length ===
        1
          ? snapPoint(
              lengthPoints[0],
              pt
            )
          : pt;

      const next =
        lengthPoints.length >=
        2
          ? [pt]
          : [
              ...lengthPoints,
              snapped,
            ];

      setLengthPoints(
        next
      );

      setActivePoint({
        kind:
          "length",

        index:
          next.length -
          1,
      });

      if (
        next.length ===
          2 &&
        pixelsPerInch
      ) {
        setLengthFt(
          distance(
            next[0],
            next[1]
          ) /
            pixelsPerInch /
            12
        );
      }

      return;
    }

    if (
      mode ===
      "height"
    ) {
      const snapped =
        heightPoints.length ===
        1
          ? snapPoint(
              heightPoints[0],
              pt
            )
          : pt;

      const next =
        heightPoints.length >=
        2
          ? [pt]
          : [
              ...heightPoints,
              snapped,
            ];

      setHeightPoints(
        next
      );

      setActivePoint({
        kind:
          "height",

        index:
          next.length -
          1,
      });

      if (
        next.length ===
          2 &&
        pixelsPerInch
      ) {
        setHeightFt(
          distance(
            next[0],
            next[1]
          ) /
            pixelsPerInch /
            12
        );
      }
    }
  };

  const onPointerMove = (
    e
  ) => {
    if (!imageLoaded) {
      return;
    }

    const pt =
      canvasPoint(e);

    if (!pt) {
      return;
    }

    if (
      downRef.current
        ?.isTouch
    ) {
      const d0 =
        downRef.current;

      const moved =
        distance(
          d0.pt,
          pt
        ) > 8;

      if (moved) {
        d0.moved =
          true;
      }

      if (
        d0.grab &&
        d0.moved
      ) {
        if (
          !dragRef.current
        ) {
          dragRef.current =
            d0.grab;
        }

        const {
          kind,
          index,
        } =
          dragRef.current;

        const arr =
          kind ===
          "stick"
            ? stickPoints
            : kind ===
              "length"
            ? lengthPoints
            : heightPoints;

        if (arr[index]) {
          const pts =
            [...arr];

          pts[index] =
            pt;

          setKindPoints(
            kind,
            pts
          );

          recompute(
            kind,
            pts
          );
        }

        setHoverPoint(
          pt
        );
      }

      return;
    }

    if (
      dragRef.current
    ) {
      const {
        kind,
        index,
      } =
        dragRef.current;

      const arr =
        kind ===
        "stick"
          ? stickPoints
          : kind ===
            "length"
          ? lengthPoints
          : heightPoints;

      if (arr[index]) {
        const pts =
          [...arr];

        pts[index] =
          pt;

        setKindPoints(
          kind,
          pts
        );

        recompute(
          kind,
          pts
        );
      }

      setHoverPoint(
        pt
      );

      return;
    }

    setHoverPoint(
      pt
    );

    setHoverGrab(
      findGrab(pt)
    );
  };

  const onPointerUp = (
    e
  ) => {
    const canvas =
      canvasRef.current;

    if (
      canvas &&
      e.pointerId !=
        null &&
      canvas.releasePointerCapture
    ) {
      try {
        canvas.releasePointerCapture(
          e.pointerId
        );
      } catch (_) {
        // Ignore pointer release errors.
      }
    }

    if (
      downRef.current
        ?.isTouch
    ) {
      const d0 =
        downRef.current;

      const upPt =
        canvasPoint(e) ||
        d0.pt;

      const wasTap =
        !d0.moved;

      if (
        wasTap &&
        !d0.grab
      ) {
        placeAt(
          upPt
        );
      }

      downRef.current =
        null;

      dragRef.current =
        null;

      setTouchActive(
        false
      );

      return;
    }

    if (
      dragRef.current
    ) {
      dragRef.current =
        null;
    }
  };

  const onPointerLeave =
    () => {
      if (
        !dragRef.current &&
        !downRef.current
      ) {
        setHoverPoint(
          null
        );

        setHoverGrab(
          null
        );
      }
    };

  /* =========================================================
     ACTIONS
     ========================================================= */

  const startHeight = () => {
    setWantHeight(
      true
    );

    setHeightPoints(
      []
    );

    setHeightFt(
      null
    );

    setMode(
      "height"
    );

    setActivePoint(
      null
    );

    setNotice(
      "Tap from the floor to the ceiling to measure the wall height."
    );
  };

  const finishLength = () => {
    setMode(
      "done"
    );

    setNotice(
      null
    );
  };

  const redoMeasurement =
    () => {
      setActivePoint(
        null
      );

      dragRef.current =
        null;

      setHoverPoint(
        null
      );

      if (
        mode ===
          "height" ||
        heightPoints.length >
          0
      ) {
        setHeightPoints(
          []
        );

        setHeightFt(
          null
        );

        setMode(
          "height"
        );

        setNotice(
          "Redo the wall height — tap from the floor to the ceiling again."
        );
      } else {
        setLengthPoints(
          []
        );

        setLengthFt(
          null
        );

        setMode(
          "length"
        );

        setNotice(
          "Redo the length — tap the two ends of the wall again."
        );
      }
    };

  /* =========================================================
     PREVIEW
     ========================================================= */

  const makePreview = () => {
    const src =
      canvasRef.current;

    if (!src) {
      return null;
    }

    try {
      const MAX_W =
        900;

      const scale =
        Math.min(
          1,
          MAX_W /
            src.width
        );

      const outW =
        Math.max(
          1,
          Math.round(
            src.width *
              scale
          )
        );

      const outH =
        Math.max(
          1,
          Math.round(
            src.height *
              scale
          )
        );

      const off =
        document.createElement(
          "canvas"
        );

      off.width =
        outW;

      off.height =
        outH;

      const octx =
        off.getContext(
          "2d"
        );

      octx.imageSmoothingEnabled =
        true;

      octx.imageSmoothingQuality =
        "high";

      octx.drawImage(
        src,
        0,
        0,
        outW,
        outH
      );

      return off.toDataURL(
        "image/jpeg",
        0.7
      );
    } catch (e) {
      console.error(
        "preview generation failed:",
        e
      );

      return null;
    }
  };

  /* =========================================================
     COMPLETE
     ========================================================= */

  const done = () => {
    /*
     * Length is required.
     *
     * Wall height is intentionally optional.
     * If the user skipped it, heightFt remains null.
     */
    onComplete &&
      onComplete({
        lengthFt:
          lengthFt ??
          null,

        heightFt:
          heightFt ??
          null,

        calibrationSource,

        preview:
          makePreview(),
      });
  };

  const lengthReady =
    lengthPoints.length ===
      2 &&
    lengthFt != null;

  const heightReady =
    heightPoints.length ===
      2 &&
    heightFt != null;

  /* =========================================================
     UI
     ========================================================= */

  return (
    <div className="kwm-root">
      <div className="kwm-head">
        <div>
          <div className="kwm-eyebrow">
            Measuring
          </div>

          <h2 className="kwm-title">
            {wallLabel}
          </h2>
        </div>

        <button
          className="kwm-btn kwm-btn--ghost"
          onClick={
            onCancel
          }
        >
          Cancel
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{
          display:
            "none",
        }}
        onChange={(e) => {
          const f =
            e.target.files?.[0];

          if (f) {
            handleUpload(
              f
            );
          }

          e.target.value =
            "";
        }}
      />

      {!imageLoaded ? (
        <div className="kwm-upload">
          <button
            className="kwm-btn kwm-btn--primary"
            onClick={() =>
              fileRef.current?.click()
            }
          >
            Upload wall photo
          </button>

          <p className="kwm-hint">
            Include the AI Stick in the shot, flat on the wall or counter.
          </p>
        </div>
      ) : (
        <div className="kwm-work">
          <div className="kwm-canvas-col">
            {capture && (
              <div
                className={
                  `kwm-capture kwm-capture--${capture.level}`
                }
              >
                <strong>
                  {capture.title}
                </strong>

                <span>
                  {capture.text}
                </span>

                <button
                  className="kwm-capture-btn"
                  onClick={() =>
                    fileRef.current?.click()
                  }
                >
                  Retake photo
                </button>
              </div>
            )}

            <div className="kwm-canvas-wrap">
              <canvas
                ref={
                  canvasRef
                }
                onPointerDown={
                  onPointerDown
                }
                onPointerMove={
                  onPointerMove
                }
                onPointerUp={
                  onPointerUp
                }
                onPointerCancel={
                  onPointerUp
                }
                onPointerLeave={
                  onPointerLeave
                }
                style={{
                  cursor:
                    dragRef.current
                      ? "grabbing"
                      : hoverGrab
                      ? "grab"
                      : "crosshair",

                  touchAction:
                    "none",
                }}
              />

              {touchActive &&
                hoverPoint &&
                canvasRef.current &&
                (() => {
                  const c =
                    canvasRef.current;

                  const dispScale =
                    c.clientWidth >
                    0
                      ? c.clientWidth /
                        c.width
                      : 1;

                  const dx =
                    hoverPoint.x *
                    dispScale;

                  const dy =
                    hoverPoint.y *
                    dispScale;

                  const LOUPE =
                    120;

                  const OFFSET =
                    90;

                  const flipBelow =
                    dy <
                    LOUPE +
                      OFFSET;

                  const top =
                    flipBelow
                      ? dy +
                        OFFSET
                      : dy -
                        OFFSET -
                        LOUPE;

                  let left =
                    dx -
                    LOUPE / 2;

                  left =
                    Math.max(
                      4,

                      Math.min(
                        left,
                        c.clientWidth -
                          LOUPE -
                          4
                      )
                    );

                  return (
                    <canvas
                      ref={
                        floatZoomRef
                      }
                      width={
                        LOUPE
                      }
                      height={
                        LOUPE
                      }
                      className="kwm-float-loupe"
                      style={{
                        left:
                          `${left}px`,

                        top:
                          `${Math.max(
                            4,
                            top
                          )}px`,
                      }}
                    />
                  );
                })()}

              {notice && (
                <div className="kwm-notice">
                  {notice}
                </div>
              )}

              {activePoint && (
                <div className="kwm-tip">
                  Drag a point to reposition, or use arrow keys to fine-tune.
                </div>
              )}
            </div>
          </div>

          <div className="kwm-panel">
            <div className="kwm-zoom-card">
              <div className="kwm-zoom-title">
                Precision zoom
              </div>

              <canvas
                ref={
                  zoomRef
                }
                width={
                  240
                }
                height={
                  240
                }
                className="kwm-zoom-canvas"
              />
            </div>

            <div className="kwm-readout">
              <div className="kwm-row">
                <span>
                  Calibration
                </span>

                <strong>
                  {pixelsPerInch
                    ? `${pixelsPerInch.toFixed(1)} px/in`
                    : "—"}
                </strong>
              </div>

              <div className="kwm-row">
                <span
                  style={{
                    color:
                      COLORS.length,
                  }}
                >
                  Length
                </span>

                <strong>
                  {lengthFt !=
                  null
                    ? feetInches(
                        lengthFt
                      )
                    : "—"}
                </strong>
              </div>

              <div className="kwm-row">
                <span
                  style={{
                    color:
                      COLORS.height,
                  }}
                >
                  Wall height
                </span>

                <strong>
                  {heightFt !=
                  null
                    ? feetInches(
                        heightFt
                      )
                    : "—"}
                </strong>
              </div>
            </div>

            <div className="kwm-actions">
              {lengthReady &&
                mode ===
                  "length" && (
                  <button
                    className="kwm-btn kwm-btn--primary"
                    onClick={
                      finishLength
                    }
                  >
                    Length looks good →
                  </button>
                )}

              {(mode ===
                "done" ||
                lengthReady) &&
                !wantHeight &&
                capture
                  ?.level !==
                  "block" && (
                  <button
                    className="kwm-btn kwm-btn--ghost"
                    onClick={
                      startHeight
                    }
                  >
                    + Measure wall height (optional)
                  </button>
                )}

              {mode ===
                "height" &&
                heightReady && (
                  <button
                    className="kwm-btn kwm-btn--primary"
                    onClick={() =>
                      setMode(
                        "done"
                      )
                    }
                  >
                    Wall height looks good →
                  </button>
                )}

              {(lengthPoints.length >
                0 ||
                heightPoints.length >
                  0) &&
                pixelsPerInch && (
                  <button
                    className="kwm-btn kwm-btn--warn"
                    onClick={
                      redoMeasurement
                    }
                  >
                    ↺ Redo measurement
                  </button>
                )}

              <button
                className="kwm-btn kwm-btn--ghost"
                onClick={() =>
                  fileRef.current?.click()
                }
              >
                Replace photo
              </button>

              <button
                className="kwm-btn kwm-btn--save"
                onClick={
                  done
                }
                disabled={
                  !lengthReady ||
                  capture
                    ?.level ===
                    "block"
                }
                title={
                  capture
                    ?.level ===
                  "block"
                    ? "Retake the photo at a higher resolution first"
                    : lengthReady
                    ? "Save this wall's measurement"
                    : "Measure the length first"
                }
              >
                Save wall ✓
              </button>
            </div>

            {busy && (
              <div className="kwm-busy">
                Detecting AI Stick…
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
